import { useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { splitTargetLink } from '../lib/alert-link'
import { PexIcon } from './icons/PexIcon'
import { useT } from '../i18n'
import { alertI18n } from '../i18n/shared/alert'
import { apiUrl } from '../lib/api'
import type { Role } from '../store/role'
import { cn } from '../lib/utils'

type Severity = 'critical' | 'warning' | 'info'

export interface AlertEntry {
  id: string
  severity: Severity
  title: string
  description: string
  audience: Role[]
  target_link?: string
}

export interface AlertsResponse {
  data: AlertEntry[]
  generated_at: string
}

const DISMISS_KEY = 'pexify_dismissed_alerts'

// Dismissed critical alerts: alert id -> the count it carried when closed.
type DismissedMap = Record<string, number>

function readDismissed(): DismissedMap {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? (parsed as DismissedMap) : {}
  } catch {
    return {}
  }
}

function writeDismissed(map: DismissedMap) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(map))
  } catch {
    // storage unavailable (private mode) — dismissal lasts for this page only
  }
}

// Alert titles lead with their count ("7 backorders overdue"); a dismissed
// alert comes back once that count grows. Count-less alerts use 0.
function alertCount(alert: AlertEntry): number {
  const match = alert.title.match(/\d+/)
  return match ? Number(match[0]) : 0
}

/**
 * AlertBanner — critical ("action required now") alerts as a slim strip under
 * the shell Topbar, above the page. Warning + info live in AlertCenter.
 *
 * Closing is remembered across reloads until the alert's count grows; an
 * alert that clears and later returns starts fresh.
 */
export function AlertBanner() {
  const t = useT(alertI18n)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [dismissed, setDismissed] = useState<DismissedMap>(readDismissed)

  const activeRole: Role | '' = pathname.startsWith('/admin')
    ? 'admin'
    : pathname.startsWith('/seller')
      ? 'seller'
      : pathname.startsWith('/supply')
        ? 'supply'
        : ''

  const { data } = useQuery<AlertsResponse>({
    queryKey: ['alerts'],
    queryFn: async () => {
      const res = await fetch(apiUrl('/alerts'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    refetchInterval: 60_000,
    enabled: activeRole !== '',
  })

  // Forget dismissals for alerts that are no longer raised at all — checked
  // once per poll response (state adjusted during render, not in an effect).
  const [prunedAt, setPrunedAt] = useState<string>()
  if (data && prunedAt !== data.generated_at) {
    setPrunedAt(data.generated_at)
    const live = new Set(data.data.map((a) => a.id))
    const stale = Object.keys(dismissed).filter((id) => !live.has(id))
    if (stale.length > 0) {
      const next = { ...dismissed }
      for (const id of stale) delete next[id]
      writeDismissed(next)
      setDismissed(next)
    }
  }

  if (!activeRole || !data) return null

  const visible = data.data.filter(
    (a) =>
      a.severity === 'critical' &&
      a.audience.includes(activeRole) &&
      !(a.id in dismissed && alertCount(a) <= dismissed[a.id]),
  )
  if (visible.length === 0) return null

  const dismissAlert = (alert: AlertEntry) => {
    const next = { ...dismissed, [alert.id]: alertCount(alert) }
    writeDismissed(next)
    setDismissed(next)
  }

  return (
    <div
      role="region"
      aria-label={t.title}
      data-testid="alert-banner"
      className="shrink-0 divide-y divide-[color-mix(in_srgb,var(--color-danger)_18%,transparent)] border-b border-[color-mix(in_srgb,var(--color-danger)_28%,var(--color-border))] bg-[color-mix(in_srgb,var(--color-danger)_7%,var(--color-surface))]"
    >
      {visible.map((alert) => {
        const body = (
          <>
            <PexIcon name="critical" size="xs" tone="danger" className="shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium text-[var(--color-text)] group-hover:underline">{alert.title}</span>
              <span className="hidden text-[var(--color-text-muted)] sm:inline">
                {' — '}
                {alert.description}
              </span>
            </span>
            {alert.target_link && (
              <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-[var(--color-text)]">
                {t.openAction}
                <PexIcon name="forward" size="xs" tone="inherit" className="h-3 w-3" />
              </span>
            )}
          </>
        )
        const rowClass =
          'flex min-w-0 flex-1 items-center gap-2.5 py-1.5 text-sm'
        return (
          <div
            key={alert.id}
            className="flex items-center gap-2 px-[var(--page-padding-x)]"
          >
            {alert.target_link ? (
              <Link
                {...splitTargetLink(alert.target_link)}
                className={cn(rowClass, 'group')}
              >
                {body}
              </Link>
            ) : (
              <div className={rowClass}>{body}</div>
            )}
            <button
              type="button"
              onClick={() => dismissAlert(alert)}
              className="shrink-0 rounded p-1 text-[var(--color-text-muted)] hover:bg-[color-mix(in_srgb,var(--color-text)_8%,transparent)] hover:text-[var(--color-text)]"
              aria-label={t.closeAriaLabel}
            >
              <PexIcon name="close" size="xs" tone="inherit" className="h-3.5 w-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
